/**
 * Pipeline Settings — three admin cards:
 *   (a) Lost reasons  — code/label/active toggle + add-row form
 *   (b) Stage SLAs    — editable target-minutes table, saved as one PUT
 *   (c) Assignment rules — list + create modal (strategy-aware config JSON) + delete
 */
import { useEffect, useMemo, useState } from 'react';
import TopBar from '../components/TopBar';
import { pipelineService } from '../lib/services';
import { useApi } from '../lib/hooks/useApi';
import { useConsultantOptions } from '../lib/useDirectory';
import { sourceLabel } from '../lib/leadSource';
import { useToast } from '../lib/toast';

const STRATEGIES = ['ROUND_ROBIN', 'LOAD_BALANCED', 'VIP', 'TERRITORY', 'SOURCE'];

/** One-line description shown under the strategy picker. */
const STRATEGY_DESC = {
  ROUND_ROBIN:   'Rotate new leads evenly across the chosen consultants.',
  LOAD_BALANCED: 'Give each lead to whichever chosen consultant has the fewest open leads.',
  VIP:           'When a lead’s score is at or above the threshold, rotate it to the chosen consultants.',
  TERRITORY:     'Route leads whose postcode starts with one of the listed prefixes to the chosen consultants.',
  SOURCE:        'Route leads from the selected sources to the chosen consultants.',
};

/** Lead sources a SOURCE rule can match (enum name → sent to the backend). */
const LEAD_SOURCE_OPTIONS = [
  'CARSALES', 'DRIVE', 'CAREXPERT', 'AUTOTRADER', 'FACEBOOK_MARKETPLACE',
  'WEB_FORM', 'PHONE', 'WALK_IN', 'WHATSAPP', 'SMS', 'EMAIL',
];

function asList(d) {
  if (Array.isArray(d)) return d;
  return d?.content ?? d?.items ?? [];
}

/* ── mock fallbacks ── */
const MOCK_LOST_REASONS = [
  { code: 'PRICE',        label: 'Price too high',          active: true },
  { code: 'BOUGHT_ELSEWHERE', label: 'Bought elsewhere',    active: true },
  { code: 'NO_STOCK',     label: 'No matching stock',       active: true },
  { code: 'FINANCE_DECLINED', label: 'Finance declined',    active: true },
  { code: 'NOT_READY',    label: 'Not ready to buy',        active: false },
];
const MOCK_SLAS = [
  // Fallback shown only when the API is unreachable. Mirrors what V47 seeds
  // into pipeline_sla_config so the placeholder never contradicts the real one.
  { stage: 'INBOUND',         targetMinutes: 15 },
  { stage: 'CONTACTED',       targetMinutes: 1440 },
  { stage: 'APPOINTMENT',     targetMinutes: 2880 },
  { stage: 'TEST_DRIVE',      targetMinutes: 2880 },
  { stage: 'NEGOTIATION',     targetMinutes: 4320 },
  { stage: 'CONFIRMED',       targetMinutes: 4320 },
  { stage: 'DELIVERY_BOOKED', targetMinutes: 10080 },
  { stage: 'RETAILED',        targetMinutes: 4320 },
];
const MOCK_RULES = [
  { id: 'rule-1', name: 'VIP straight to senior',    strategy: 'VIP',           priority: 1, active: true },
  { id: 'rule-2', name: 'Carsales round-robin',      strategy: 'SOURCE',        priority: 5, active: true },
  { id: 'rule-3', name: 'Default load balance',      strategy: 'LOAD_BALANCED', priority: 9, active: true },
];

/* ── create-rule modal ── */
const EMPTY_RULE = {
  name: '', strategy: 'ROUND_ROBIN', priority: 5, active: true,
  consultantIds: [], scoreThreshold: 80, sources: [], postcodePrefixes: '', maxOpenLeads: '',
};

function NewRuleModal({ open, onClose, onCreated }) {
  const { toast } = useToast();
  const { options: consultants } = useConsultantOptions();
  const [form, setForm] = useState(EMPTY_RULE);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => { if (open) setForm(EMPTY_RULE); }, [open]);

  const toggle = (key, id) => setForm((f) => {
    const has = f[key].includes(id);
    return { ...f, [key]: has ? f[key].filter((x) => x !== id) : [...f[key], id] };
  });

  const needsSources = form.strategy === 'SOURCE';
  const needsPostcodes = form.strategy === 'TERRITORY';
  const isVip = form.strategy === 'VIP';
  const isLoad = form.strategy === 'LOAD_BALANCED';

  const canSubmit = useMemo(() => {
    if (!form.name.trim()) return false;
    if (needsSources && form.sources.length === 0) return false;
    if (needsPostcodes && !form.postcodePrefixes.trim()) return false;
    return true;
  }, [form, needsSources, needsPostcodes]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    // Build the config the AssignmentRulesEngine reads — no hand-written JSON.
    const config = {};
    if (form.consultantIds.length) config.consultantIds = form.consultantIds;   // empty = all active consultants
    if (isVip) config.scoreThreshold = Number(form.scoreThreshold) || 80;
    if (needsSources) config.sources = form.sources;
    if (needsPostcodes) {
      config.postcodePrefixes = form.postcodePrefixes
        .split(',').map((s) => s.trim()).filter(Boolean);
    }
    if (isLoad && form.maxOpenLeads) config.maxOpenLeads = Number(form.maxOpenLeads);

    setBusy(true);
    try {
      await pipelineService.createAssignmentRule({
        name: form.name.trim(),
        strategy: form.strategy,
        priority: Number(form.priority) || 0,
        active: !!form.active,
        config: Object.keys(config).length ? config : null,
      });
      toast('Assignment rule created', { variant: 'success' });
      onCreated();
      onClose();
    } catch (err) {
      toast(`Create failed: ${err?.response?.data?.message || err?.message || 'unknown error'}`, { variant: 'danger' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <form className="modal" style={{ width: 'min(560px, 94vw)', maxHeight: '92vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="modal-header">
          <div className="modal-title">New assignment rule</div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="form-field">
            <label>Name *</label>
            <input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. VIP straight to senior" autoFocus required />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-field">
              <label>Strategy</label>
              <select value={form.strategy} onChange={(e) => set('strategy', e.target.value)}>
                {STRATEGIES.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
              </select>
            </div>
            <div className="form-field">
              <label>Priority (1 = first)</label>
              <input type="number" min={1} max={99} value={form.priority} onChange={(e) => set('priority', e.target.value)} />
            </div>
          </div>
          <div className="mono text-xs text-muted" style={{ marginTop: -6 }}>{STRATEGY_DESC[form.strategy]}</div>

          {/* Strategy-specific criteria */}
          {isVip && (
            <div className="form-field">
              <label>Apply when lead score is at least</label>
              <input type="number" min={0} max={100} value={form.scoreThreshold}
                     onChange={(e) => set('scoreThreshold', e.target.value)} style={{ width: 120 }} />
            </div>
          )}
          {isLoad && (
            <div className="form-field">
              <label>Max open leads per consultant (optional)</label>
              <input type="number" min={1} value={form.maxOpenLeads} placeholder="no cap"
                     onChange={(e) => set('maxOpenLeads', e.target.value)} style={{ width: 120 }} />
            </div>
          )}
          {needsPostcodes && (
            <div className="form-field">
              <label>Postcode prefixes *</label>
              <input value={form.postcodePrefixes} onChange={(e) => set('postcodePrefixes', e.target.value)}
                     placeholder="e.g. 20, 2150, 3000" />
              <span className="mono text-xs text-muted">Comma-separated. A lead matches if its postcode starts with any prefix.</span>
            </div>
          )}
          {needsSources && (
            <div className="form-field">
              <label>Lead sources *</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {LEAD_SOURCE_OPTIONS.map((s) => {
                  const on = form.sources.includes(s);
                  return (
                    <button key={s} type="button" onClick={() => toggle('sources', s)}
                      className={`btn small ${on ? 'primary' : ''}`} style={{ padding: '2px 10px' }}>
                      {sourceLabel(s)}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Consultant pool — the thing that used to be raw JSON */}
          <div className="form-field">
            <label>Consultants {form.consultantIds.length > 0 && <span className="text-muted">· {form.consultantIds.length} selected</span>}</label>
            <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
              <button type="button" className="btn small" onClick={() => set('consultantIds', consultants.map((c) => c.id))}>Select all</button>
              <button type="button" className="btn small" onClick={() => set('consultantIds', [])}>Clear</button>
            </div>
            <div style={{ maxHeight: 190, overflow: 'auto', border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 8, padding: 6 }}>
              {consultants.length === 0 && <div className="mono text-xs text-muted" style={{ padding: 8 }}>No consultants found for this dealership.</div>}
              {consultants.map((c) => (
                <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 6px', cursor: 'pointer', borderRadius: 6 }}>
                  <input type="checkbox" checked={form.consultantIds.includes(c.id)} onChange={() => toggle('consultantIds', c.id)} />
                  <span>{c.primary}</span>
                </label>
              ))}
            </div>
            <span className="mono text-xs text-muted">Leave empty to use all active consultants.</span>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={form.active} onChange={(e) => set('active', e.target.checked)} />
            Active
          </label>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn small" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn small primary" disabled={busy || !canSubmit}>
            {busy ? 'Creating…' : 'Create rule'}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ── page ── */
export default function PipelineSettings() {
  const { toast } = useToast();

  const lostQ = useApi(() => pipelineService.lostReasons(), { devFallback: MOCK_LOST_REASONS });
  const slaQ = useApi(() => pipelineService.slaConfig(), { devFallback: MOCK_SLAS });
  const rulesQ = useApi(() => pipelineService.assignmentRules(), { devFallback: MOCK_RULES });

  const lostReasons = asList(lostQ.data);
  const rules = asList(rulesQ.data);

  /* Lost reasons */
  const [newReason, setNewReason] = useState({ code: '', label: '' });
  const addReason = async (e) => {
    e.preventDefault();
    if (!newReason.code.trim() || !newReason.label.trim()) return;
    try {
      await pipelineService.createLostReason({
        code: newReason.code.trim().toUpperCase().replace(/\s+/g, '_'),
        label: newReason.label.trim(),
        active: true,
      });
      toast('Lost reason added', { variant: 'success' });
      setNewReason({ code: '', label: '' });
      lostQ.refetch();
    } catch (err) {
      toast(`Add failed: ${err?.response?.data?.message || err?.message || 'unknown error'}`, { variant: 'danger' });
    }
  };
  const toggleReason = async (r) => {
    try {
      await pipelineService.updateLostReason(r.code, { active: !r.active });
      toast(`${r.code} ${r.active ? 'deactivated' : 'activated'}`, { variant: 'success' });
      lostQ.refetch();
    } catch (err) {
      toast(`Update failed: ${err?.response?.data?.message || err?.message || 'unknown error'}`, { variant: 'danger' });
    }
  };

  /* SLAs — local editable copy, saved in one PUT */
  const [slaRows, setSlaRows] = useState(MOCK_SLAS);
  const [slaDirty, setSlaDirty] = useState(false);
  const [slaSaving, setSlaSaving] = useState(false);
  useEffect(() => {
    const rows = asList(slaQ.data);
    if (rows.length) {
      setSlaRows(rows);
      setSlaDirty(false);
    }
  }, [slaQ.data]);
  const setSlaMinutes = (stage, minutes) => {
    setSlaRows((rows) => rows.map((r) => (r.stage === stage ? { ...r, targetMinutes: minutes } : r)));
    setSlaDirty(true);
  };
  const saveSlas = async () => {
    setSlaSaving(true);
    try {
      const payload = slaRows.map((r) => ({ ...r, targetMinutes: Number(r.targetMinutes) || 0 }));
      await pipelineService.updateSlaConfig(payload);
      toast('Stage SLAs saved', { variant: 'success' });
      setSlaDirty(false);
      slaQ.refetch();
    } catch (err) {
      toast(`Save failed: ${err?.response?.data?.message || err?.message || 'unknown error'}`, { variant: 'danger' });
    } finally {
      setSlaSaving(false);
    }
  };

  /* Assignment rules */
  const [ruleModalOpen, setRuleModalOpen] = useState(false);
  const toggleRule = async (r) => {
    try {
      await pipelineService.updateAssignmentRule(r.id, { active: !r.active });
      toast(`Rule "${r.name}" ${r.active ? 'deactivated' : 'activated'}`, { variant: 'success' });
      rulesQ.refetch();
    } catch (err) {
      toast(`Update failed: ${err?.response?.data?.message || err?.message || 'unknown error'}`, { variant: 'danger' });
    }
  };
  const deleteRule = async (r) => {
    try {
      await pipelineService.deleteAssignmentRule(r.id);
      toast(`Rule "${r.name}" deleted`, { variant: 'success' });
      rulesQ.refetch();
    } catch (err) {
      toast(`Delete failed: ${err?.response?.data?.message || err?.message || 'unknown error'}`, { variant: 'danger' });
    }
  };

  const fmtSla = (mins) => {
    const m = Number(mins) || 0;
    if (m < 60) return `${m} min`;
    if (m < 1440) return `${Math.round(m / 60)} h`;
    return `${Math.round(m / 1440)} d`;
  };

  return (
    <>
      <TopBar crumbs={['Pipeline Settings']} />
      <div className="app-content" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div className="page-header">
          <div>
            <div className="page-header-title">Pipeline Settings</div>
            <div className="page-header-sub">Stages, SLAs, lost reasons and assignment rules</div>
          </div>
        </div>

        {/* (a) Lost reasons */}
        <div className="card" style={{ padding: 0 }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14 }}>Lost reasons</div>
              <div className="mono text-xs text-muted">Codes consultants pick when marking a lead lost</div>
            </div>
            {lostQ.loading && <span className="mono text-xs text-muted" style={{ marginLeft: 'auto' }}>loading…</span>}
          </div>
          <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr><th style={{ width: 220 }}>Code</th><th>Label</th><th style={{ width: 120 }}>Active</th></tr>
            </thead>
            <tbody>
              {lostReasons.map((r) => (
                <tr key={r.code}>
                  <td className="mono text-xs">{r.code}</td>
                  <td>{r.label}</td>
                  <td>
                    <button
                      className={`pill filled ${r.active ? 'success' : 'danger'}`}
                      style={{ cursor: 'pointer', border: 'none' }}
                      onClick={() => toggleReason(r)}
                      title="Toggle active"
                    >{r.active ? 'Active' : 'Inactive'}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          <form
            onSubmit={addReason}
            style={{ display: 'flex', gap: 8, padding: 12, borderTop: '1px solid var(--border-subtle)', alignItems: 'flex-end', flexWrap: 'wrap' }}
          >
            <div className="form-field" style={{ width: 200 }}>
              <label>Code</label>
              <input value={newReason.code} onChange={(e) => setNewReason((f) => ({ ...f, code: e.target.value }))} placeholder="PRICE" />
            </div>
            <div className="form-field" style={{ flex: 1, minWidth: 220 }}>
              <label>Label</label>
              <input value={newReason.label} onChange={(e) => setNewReason((f) => ({ ...f, label: e.target.value }))} placeholder="Price too high" />
            </div>
            <button type="submit" className="btn small primary" disabled={!newReason.code.trim() || !newReason.label.trim()}>
              + Add reason
            </button>
          </form>
        </div>

        {/* (b) Stage SLAs */}
        <div className="card" style={{ padding: 0 }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14 }}>Stage SLAs</div>
              <div className="mono text-xs text-muted">Target response time per pipeline stage (minutes)</div>
            </div>
            <span style={{ marginLeft: 'auto' }} />
            {slaQ.loading && <span className="mono text-xs text-muted">loading…</span>}
            <button className="btn small primary" onClick={saveSlas} disabled={!slaDirty || slaSaving}>
              {slaSaving ? 'Saving…' : slaDirty ? 'Save SLAs' : 'Saved'}
            </button>
          </div>
          <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr><th>Stage</th><th style={{ width: 200 }}>Target (minutes)</th><th style={{ width: 120 }}>≈</th></tr>
            </thead>
            <tbody>
              {slaRows.map((r) => (
                <tr key={r.stage}>
                  <td className="mono text-xs">{r.stage}</td>
                  <td>
                    <input
                      type="number"
                      min={0}
                      value={r.targetMinutes}
                      onChange={(e) => setSlaMinutes(r.stage, e.target.value)}
                      style={{
                        width: 120, padding: '4px 8px', borderRadius: 6,
                        border: '1px solid var(--border-color)',
                        background: 'var(--bg-secondary)', color: 'var(--text-primary)',
                      }}
                    />
                  </td>
                  <td className="text-muted">{fmtSla(r.targetMinutes)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>

        {/* (c) Assignment rules */}
        <div className="card" style={{ padding: 0 }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14 }}>Assignment rules</div>
              <div className="mono text-xs text-muted">Evaluated in priority order for every new lead</div>
            </div>
            <span style={{ marginLeft: 'auto' }} />
            {rulesQ.loading && <span className="mono text-xs text-muted">loading…</span>}
            <button className="btn small primary" onClick={() => setRuleModalOpen(true)}>+ New rule</button>
          </div>
          {rules.length === 0 ? (
            <div className="empty-state" style={{ padding: 32 }}>
              <div className="icon">⚙</div>
              <div className="title">No assignment rules</div>
              <div className="desc">New leads fall back to manual assignment. Create a rule to automate routing.</div>
            </div>
          ) : (
            <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th><th>Strategy</th><th style={{ width: 100 }}>Priority</th>
                  <th style={{ width: 120 }}>Active</th><th style={{ width: 100 }}></th>
                </tr>
              </thead>
              <tbody>
                {[...rules].sort((a, b) => (a.priority ?? 99) - (b.priority ?? 99)).map((r) => (
                  <tr key={r.id}>
                    <td style={{ fontWeight: 600 }}>{r.name}</td>
                    <td><span className="pill info">{(r.strategy || '—').replace('_', ' ')}</span></td>
                    <td className="mono text-xs">{r.priority ?? '—'}</td>
                    <td>
                      <button
                        className={`pill filled ${r.active ? 'success' : 'danger'}`}
                        style={{ cursor: 'pointer', border: 'none' }}
                        onClick={() => toggleRule(r)}
                        title="Toggle active"
                      >{r.active ? 'Active' : 'Inactive'}</button>
                    </td>
                    <td>
                      <button className="btn small danger" onClick={() => deleteRule(r)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>

      <NewRuleModal open={ruleModalOpen} onClose={() => setRuleModalOpen(false)} onCreated={rulesQ.refetch} />
    </>
  );
}
